import { useCallback, useRef, useState } from "react";

/**
 * Multi-step undo stack scoped to a single editor session (M7.5 C2).
 *
 * Cap at 10 entries — beyond which the oldest snapshot gets dropped.
 * The stack is NOT persisted: switching source (tactical board ↔
 * telestrator), opening a different clip-edit context, or remounting
 * the editor wipes it. That's intentional — undoing across an
 * unrelated edit context would be confusing (the M7 modal-stacking
 * lesson applied to history).
 *
 * Usage:
 *   const undo = useUndoStack<BoardScene>(initial, { maxDepth: 10 });
 *   undo.push(currentScene);    // before applying a change
 *   const previous = undo.pop(); // returns the just-saved snapshot
 *   undo.clear();                // on source / context switch
 *
 * Returning the popped value (rather than calling a `restore`
 * callback) lets the caller decide whether the snapshot can still
 * be applied — e.g. if the editor remounted with a different
 * scene, the popped snapshot is stale and the caller can discard.
 */

export type UndoStack<T> = {
  /** Push a snapshot onto the stack. Drops the oldest if at cap. */
  push: (snapshot: T) => void;
  /** Pop the most-recent snapshot, or `undefined` when empty. */
  pop: () => T | undefined;
  /** Wipe the stack — call on context switches. */
  clear: () => void;
  /** Number of entries in the stack — drives the Undo button's
   * disabled state. */
  depth: number;
};

type Options = {
  /** Default 10. */
  maxDepth?: number;
};

export function useUndoStack<T>(opts: Options = {}): UndoStack<T> {
  const maxDepth = opts.maxDepth ?? 10;
  // The stack lives in a ref so `push` / `pop` are stable across
  // renders. A separate `depth` state re-renders consumers (the
  // button's `disabled` state) when the stack changes.
  const stackRef = useRef<T[]>([]);
  const [depth, setDepth] = useState(0);

  const push = useCallback(
    (snapshot: T) => {
      const next = [...stackRef.current, snapshot];
      // Drop the oldest if we exceed the cap.
      if (next.length > maxDepth) next.shift();
      stackRef.current = next;
      setDepth(next.length);
    },
    [maxDepth],
  );

  const pop = useCallback((): T | undefined => {
    const current = stackRef.current;
    if (current.length === 0) return undefined;
    const last = current[current.length - 1];
    stackRef.current = current.slice(0, -1);
    setDepth(stackRef.current.length);
    return last;
  }, []);

  const clear = useCallback(() => {
    stackRef.current = [];
    setDepth(0);
  }, []);

  return { push, pop, clear, depth };
}
